import React, { useState, useRef, useEffect } from 'react';
import './BuildYourBundle.css';
import { getWineClubData } from './api/wineClubSettingApi';
import {
  getFullWineClubInfo,
  getCustomerId,
  updateWineClubSubscriptionOrderLines,
} from './api/wineClubInfoApi';
import { getItemType } from './utils/wineClubItemUtils';
import { buildSubscriptionOrderLinesFromDclubBundle } from './utils/wineClubProductDraftUtils';
import {
  buildTiersFromSettings,
  getTierForCount,
  getNextTier,
  computeShippingCost,
  formatTierShippingPreview,
} from './utils/wineClubTierUtils';

// Storefront API config – does not touch Shopify native cart
const STOREFRONT_TOKEN = "121068de739d2bd385f88917ef9b3d22";
const STORE_DOMAIN = "https://duplin-winery.myshopify.com"; // Or use subdomain: "your-store" with URL https://${STORE_DOMAIN}.myshopify.com/api/...
const GRAPHQL_URL = `${STORE_DOMAIN.replace(/\/$/, "")}/api/2024-01/graphql.json`;

/** Storefront API requires GID format. Converts numeric ID to "gid://shopify/ProductVariant/123". */
function toVariantGid(variantId) {
  const id = String(variantId || "").trim();
  if (!id) return "";
  if (id.startsWith("gid://")) return id;
  return `gid://shopify/ProductVariant/${id}`;
}

/** Normalize variant id for comparison (GID or numeric). */
function normalizeVariantId(variantId) {
  const s = String(variantId || "").trim();
  if (!s) return "";
  if (s.startsWith("gid://")) return s;
  return `gid://shopify/ProductVariant/${s}`;
}

/** Match subscription line item to a catalog product by variant id. */
function findProductInPool(lineItem, pool) {
  if (!Array.isArray(pool) || !pool.length || !lineItem?.variantId) return null;
  const target = normalizeVariantId(lineItem.variantId);
  return (
    pool.find((p) => normalizeVariantId(p?.variantId ?? p?.id) === target) || null
  );
}

function lineItemToSyntheticProduct(lineItem, prefix) {
  const baseId = lineItem.lineItemId || lineItem.variantId || "line";
  return {
    id: `${prefix}-${baseId}`,
    variantId: String(lineItem.variantId ?? "").trim(),
    title: lineItem.title,
    image: lineItem.image,
    price:
      lineItem.itemPrice != null && lineItem.itemPrice !== ""
        ? lineItem.itemPrice
        : lineItem.price,
    handle: lineItem.handle || "",
    tags: Array.isArray(lineItem.tags) ? lineItem.tags : [],
  };
}

/**
 * Get selling plan ID for a variant from plan.products (product-wise sellingPlan).
 * @param {string} variantId - variant GID or numeric
 * @param {{ products?: Array<{ variants?: Array<{ variantId: string }>, sellingPlan?: string }> }|null} plan
 * @returns {string|undefined}
 */
function getSellingPlanIdForVariant(variantId, plan) {
  if (!plan?.products?.length || !variantId) return undefined;
  const normalized = normalizeVariantId(variantId);
  for (const product of plan.products) {
    const variants = product?.variants ?? [];
    const found = variants.some((v) => normalizeVariantId(v?.variantId) === normalized);
    if (found && product?.sellingPlan) return product.sellingPlan;
  }
  return undefined;
}

/** Total bottles/lines for "Your Picks" after merge (sum of quantities on your_pick lines). */
function countYourPickBottles(selectedItems) {
  if (!Array.isArray(selectedItems)) return 0;
  return selectedItems
    .filter((i) => i.lineType === "your_pick")
    .reduce((sum, i) => sum + Math.max(1, parseInt(i.quantity, 10) || 1), 0);
}

/**
 * Creates a cart via Storefront API and redirects to checkout.
 * Product-wise sellingPlanId from plan.products (match variantId) is set per line.
 * @param {Array<{ variantId: string, quantity: number }>} selectedItems
 * @param {{ products?: Array<{ variants?: Array<{ variantId: string }>, sellingPlan?: string }> }|null} plan - wine club plan for selling plan IDs
 */
async function handleCheckout(selectedItems, plan = null) {
  if (!selectedItems?.length) {
    throw new Error("No items to checkout");
  }

  const yourPickBottles = countYourPickBottles(selectedItems);
  const minYourPicks = 2;
  if (yourPickBottles < minYourPicks) {
    throw new Error(`Add at least ${minYourPicks} Your Picks to continue.`);
  }
  if (yourPickBottles % 2 !== 0) {
    throw new Error("Please keep Your Picks in an even number to continue checkout.");
  }

  const lines = selectedItems.map(({ variantId, quantity, lineType }) => {
    const line = {
      merchandiseId: toVariantGid(variantId),
      quantity: Math.max(1, parseInt(quantity, 10) || 1),
      attributes: [{ key: "wine_club_subscription", value: lineType || "your_pick" }],
    };
    const sellingPlanId = getSellingPlanIdForVariant(variantId, plan);
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;
    return line;
  });

  const mutation = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const response = await fetch(GRAPHQL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query: mutation,
      variables: {
        input: {
          lines,
          attributes: [{ key: "order_type", value: "wine_club_subscription" }],
        },
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Checkout request failed: ${response.status} ${response.statusText}`);
  }

  const json = await response.json();

  if (json.errors?.length) {
    const messages = json.errors.map((e) => e.message).join("; ");
    throw new Error(messages);
  }

  const { cartCreate } = json.data || {};
  const userErrors = cartCreate?.userErrors || [];
  if (userErrors.length) {
    const messages = userErrors.map((e) => e.message).join("; ");
    throw new Error(messages);
  }

  const checkoutUrl = cartCreate?.cart?.checkoutUrl;
  if (!checkoutUrl) {
    throw new Error("No checkout URL returned");
  }

  window.location.href = checkoutUrl;
}

/**
 * Set of variant IDs that exist in backend plan (have sellingPlanId for subscription).
 * Used to filter Liquid script products: only show those that match plan.
 * @param {{ products?: Array<{ variants?: Array<{ variantId?: string, id?: string }>, variantId?: string }> }|null} plan
 * @returns {Set<string>}
 */
function getPlanVariantIds(plan) {
  const set = new Set();
  if (!plan?.products?.length) return set;
  for (const p of plan.products) {
    const vid = p.variantId || p.variants?.[0]?.variantId || p.variants?.[0]?.id;
    if (vid) set.add(normalizeVariantId(vid));
    if (Array.isArray(p.variants)) {
      for (const v of p.variants) {
        const id = v?.variantId || v?.id;
        if (id) set.add(normalizeVariantId(id));
      }
    }
  }
  return set;
}

const DATA_SCRIPT_ID = "wine-products-data";

/** Read and parse products from Liquid-injected script tag. Returns [] if missing or invalid. */
function getProductsFromData() {
  if (typeof document === "undefined") return [];
  const el = document.getElementById(DATA_SCRIPT_ID);
  if (!el || typeof el.textContent !== "string") return [];
  const raw = el.textContent.trim();
  if (!raw) return [];
  try {
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/** Whether product has wine_add_on tag (for Perfect Pairings / add-ons). */
function isWineAddOn(product) {
  const tags = product?.tags;
  return Array.isArray(tags) && tags.includes("wine_add_on");
}

/** Whether product is sold out (not addable to bundle). */
function isSoldOut(product) {
  if (product?.available === false || product?.soldOut === true) return true;
  if (product?.availableForSale === false) return true;
  return false;
}

/** Normalize price from string to number for calculations. */
function parsePrice(price) {
  if (price == null) return 0;
  const n = typeof price === "string" ? parseFloat(price) : Number(price);
  return Number.isFinite(n) ? n : 0;
}

/** Whether product has Duplin Pick tag. */
function isDuplinPick(product) {
  const tags = product?.tags;
  return Array.isArray(tags) && tags.includes("duplin-pick");
}

/** Filter products by selected filter; "all" returns all with duplin-pick first. */
function filterProductsByTag(products, filter) {
  if (!Array.isArray(products) || !products.length) return [];
  if (filter === "all") {
    return [...products].sort((a, b) => {
      const aPick = isDuplinPick(a);
      const bPick = isDuplinPick(b);
      if (aPick && !bPick) return -1;
      if (!aPick && bPick) return 1;
      return 0;
    });
  }
  const tagMap = {
    red: "red-wine",
    white: "white-wine",
    speciality: "speciality-wine",
  };
  const tag = tagMap[filter];
  if (!tag) return products;
  return products.filter((p) => Array.isArray(p?.tags) && p.tags.includes(tag));
}

const BuildYourBundle = () => {
  const [products, setProducts] = useState([]);
  const [bundleItems, setBundleItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 750);
  const [pairingScrollIndex, setPairingScrollIndex] = useState(0);
  const [addons, setAddons] = useState([]); // New state for add-on items
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const checkoutErrorRef = useRef(null);
  const [wineClubSettings, setWineClubSettings] = useState([]);
  const [wineClubPlan, setWineClubPlan] = useState(null);
  const [wineClubSettingsLoading, setWineClubSettingsLoading] = useState(true);
  const [wineClubSubscriptionInfo, setWineClubSubscriptionInfo] = useState(null);
  const [subscriptionInfoLoading, setSubscriptionInfoLoading] = useState(false);
  const [subscriptionInfoError, setSubscriptionInfoError] = useState(null);
  const [subscriptionSaveError, setSubscriptionSaveError] = useState(null);
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);
  /** After hydrating from latestSubscriptionOrder, Duplin quantities are explicit (0 = omit line). */
  const [subscriptionOrderHydrated, setSubscriptionOrderHydrated] = useState(false);
  /** Duplin pick lines from the order that did not match catalog duplin products. */
  const [extraDuplinFromOrder, setExtraDuplinFromOrder] = useState([]);
  const subscriptionHydrateKeyRef = useRef(null);
  const MINIMUM_BOTTLES = 2;

  const latestSubscriptionOrder =
    wineClubSubscriptionInfo?.latestSubscriptionOrder ??
    wineClubSubscriptionInfo?.latest_subscription_order;
  const subscriptionContractId =
    wineClubSubscriptionInfo?.subscriptionContractId ??
    wineClubSubscriptionInfo?.membershipDetails?.adminGraphqlApiId ??
    wineClubSubscriptionInfo?.subscription_contract_id;
  const subscriptionLooksActive =
    wineClubSubscriptionInfo?.activeSubscription === true ||
    String(wineClubSubscriptionInfo?.membershipDetails?.status).toLowerCase() === "active";
  /** Active member: only Save (no checkout). New signup: only Proceed to checkout. */
  const hasActiveWineSubscription =
    Boolean(subscriptionContractId) && subscriptionLooksActive;

  const wineClubTiers = React.useMemo(
    () => buildTiersFromSettings(wineClubSettings),
    [wineClubSettings]
  );

  /** Variant IDs that exist in backend plan (have sellingPlanId). Only show Liquid products that match. */
  const planVariantIds = React.useMemo(() => getPlanVariantIds(wineClubPlan), [wineClubPlan]);

  /** Liquid script products that match backend plan (so they have sellingPlanId for subscription checkout). */
  const displayProducts = React.useMemo(() => {
    if (!Array.isArray(products) || !products.length) return [];
    if (!planVariantIds.size) return [];
    return products.filter((p) => {
      const vid = p?.variantId ?? p?.id;
      return vid && planVariantIds.has(normalizeVariantId(vid));
    });
  }, [products, planVariantIds]);

  /** Main grid / bundle: plan-matched products excluding wine_add_on (add-ons only in Perfect Pairings). */
  const productsForGrid = React.useMemo(
    () => (Array.isArray(displayProducts) ? displayProducts.filter((p) => !isWineAddOn(p)) : []),
    [displayProducts]
  );

  const duplinPicks = React.useMemo(
    () => (Array.isArray(productsForGrid) ? productsForGrid.filter(isDuplinPick) : []),
    [productsForGrid]
  );

  /** Products with wine_add_on tag – only shown in Perfect Pairings section, not in main list. */
  const addonProducts = React.useMemo(
    () => (Array.isArray(displayProducts) ? displayProducts.filter(isWineAddOn) : []),
    [displayProducts]
  );

  useEffect(() => {
    setProducts(getProductsFromData());
  }, []);

  useEffect(() => {
    let cancelled = false;
    setWineClubSettingsLoading(true);
    getWineClubData()
      .then(({ settings: settingsList, plan: planData }) => {
        if (cancelled) return;
        setWineClubSettings(Array.isArray(settingsList) ? settingsList : []);
        setWineClubPlan(planData ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          // Keep defaults/fallback behavior if API fails.
          void err;
          setWineClubSettings([]);
          setWineClubPlan(null);
        }
      })
      .finally(() => {
        if (!cancelled) setWineClubSettingsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cid = getCustomerId();
    if (cid == null || cid === "") {
      setWineClubSubscriptionInfo(null);
      setSubscriptionInfoError(null);
      return;
    }
    let cancelled = false;
    setSubscriptionInfoLoading(true);
    setSubscriptionInfoError(null);
    getFullWineClubInfo(cid)
      .then((data) => {
        if (!cancelled) setWineClubSubscriptionInfo(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setWineClubSubscriptionInfo(null);
          setSubscriptionInfoError(err?.message || "Could not load wine club subscription");
        }
      })
      .finally(() => {
        if (!cancelled) setSubscriptionInfoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const order = latestSubscriptionOrder;
    const items = order?.items ?? order?.lineItems ?? order?.line_items;
    if (!Array.isArray(items) || items.length === 0) {
      setSubscriptionOrderHydrated(false);
      setExtraDuplinFromOrder([]);
      subscriptionHydrateKeyRef.current = null;
      return;
    }
    const catalogReady = !wineClubSettingsLoading;
    const key = `${order.orderId ?? order.order_id ?? ""}-${order.updatedAt ?? order.updated_at ?? ""}-${catalogReady ? "ready" : "loading"}`;
    if (subscriptionHydrateKeyRef.current === key) return;

    const poolMain =
      productsForGrid.length > 0
        ? productsForGrid
        : displayProducts.length > 0
          ? displayProducts.filter((p) => !isWineAddOn(p))
          : products.filter((p) => !isWineAddOn(p));
    const poolAddons =
      addonProducts.length > 0
        ? addonProducts
        : displayProducts.filter(isWineAddOn);
    const poolDuplin = duplinPicks;

    const duplinQtyReset = {};
    duplinPicks.forEach((p) => {
      duplinQtyReset[p.id] = 0;
    });

    const newBundle = [];
    const newAddons = [];
    const extraDuplin = [];
    const qtyMap = { ...duplinQtyReset };

    items.forEach((item, i) => {
      const type = getItemType(item, i, items);
      const q = Math.max(1, Number(item.quantity ?? item.currentQuantity) || 1);

      if (type === "add_on") {
        const catalog =
          findProductInPool(item, poolAddons) ||
          findProductInPool(item, addonProducts) ||
          findProductInPool(item, displayProducts) ||
          findProductInPool(item, products);
        const base = catalog
          ? { ...catalog, isAddon: true }
          : { ...lineItemToSyntheticProduct(item, "addon"), isAddon: true };
        for (let j = 0; j < q; j++) {
          newAddons.push({
            ...base,
            id: `${String(base.variantId)}-addon-${item.lineItemId || item.variantId}-${j}`,
            isAddon: true,
          });
        }
        return;
      }

      if (type === "your_pick") {
        const catalog =
          findProductInPool(item, poolMain) ||
          findProductInPool(item, displayProducts) ||
          findProductInPool(item, products);
        const base = catalog || lineItemToSyntheticProduct(item, "your");
        for (let j = 0; j < q; j++) {
          newBundle.push({
            ...base,
            id: `pick-${item.lineItemId || item.variantId}-${j}`,
          });
        }
        return;
      }

      if (type === "duplin_pick") {
        const catalog = findProductInPool(item, poolDuplin);
        if (catalog) {
          qtyMap[catalog.id] = (qtyMap[catalog.id] || 0) + q;
        } else {
          extraDuplin.push({
            id: `duplin-order-${item.lineItemId || item.variantId}`,
            variantId: String(item.variantId ?? "").trim(),
            title: item.title,
            image: item.image,
            price: item.itemPrice,
            quantity: q,
            handle: "",
          });
        }
      }
    });

    setQuantities((prev) => ({ ...prev, ...qtyMap }));
    setBundleItems(newBundle);
    setAddons(newAddons);
    setExtraDuplinFromOrder(extraDuplin);
    setSubscriptionOrderHydrated(true);
    subscriptionHydrateKeyRef.current = key;
  }, [
    latestSubscriptionOrder,
    wineClubSettingsLoading,
    productsForGrid,
    displayProducts,
    addonProducts,
    duplinPicks,
    products,
  ]);

  useEffect(() => {
    if (checkoutError && checkoutErrorRef.current) {
      checkoutErrorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [checkoutError]);

  const getDuplinQuantityForCheckout = (p) => {
    if (subscriptionOrderHydrated) {
      return Math.max(0, Number(quantities[p.id]) || 0);
    }
    const q = quantities[p.id];
    return q != null && q !== "" ? Math.max(1, Number(q) || 1) : 1;
  };

  const getSelectedItemsFromBundle = () => {
    const byVariantId = {};
    const addLine = (product, qty, lineType) => {
      const vid = product?.variantId ?? product?.id;
      const vidStr = vid != null ? String(vid).trim() : "";
      if (!vidStr) return;
      const key = normalizeVariantId(vidStr);
      if (!byVariantId[key]) {
        byVariantId[key] = { variantId: vidStr, quantity: 0, lineType };
      }
      byVariantId[key].quantity += qty || 1;
    };
    duplinPicks.forEach((p) => {
      const dq = getDuplinQuantityForCheckout(p);
      if (dq < 1) return;
      addLine(p, dq, "duplin_pick");
    });
    extraDuplinFromOrder.forEach((row) => {
      const q = Math.max(1, Number(row.quantity) || 1);
      const vid = row.variantId;
      if (!vid) return;
      const key = normalizeVariantId(vid);
      if (!byVariantId[key]) {
        byVariantId[key] = { variantId: String(vid), quantity: 0, lineType: "duplin_pick" };
      }
      byVariantId[key].quantity += q;
    });
    bundleItems.forEach((p) => addLine(p, quantities[p.id] || getYourPicksQuantity(p) || 1, "your_pick"));
    addons.forEach((p) => addLine(p, 1, "add_on"));
    return Object.values(byVariantId);
  };

  const onCheckoutClick = async () => {
    setCheckoutError(null);
    setIsCheckoutLoading(true);
    try {
      const selectedItems = getSelectedItemsFromBundle();
      const yourPickCount = countYourPickBottles(selectedItems);
      if (yourPickCount < MINIMUM_BOTTLES) {
        setCheckoutError(`Add at least ${MINIMUM_BOTTLES} Your Picks to continue.`);
        return;
      }
      if (yourPickCount % 2 !== 0) {
        setCheckoutError("Please keep Your Picks in an even number to continue checkout.");
        return;
      }
      if (!selectedItems.length) {
        setCheckoutError("Add variantId (Shopify GID) to your products to enable checkout.");
        return;
      }
      await handleCheckout(selectedItems, wineClubPlan);
    } catch (err) {
      setCheckoutError(err.message || "Checkout failed.");
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const onSaveSubscriptionOrder = async () => {
    setSubscriptionSaveError(null);
    setSubscriptionSaving(true);
    try {
      const lines = buildSubscriptionOrderLinesFromDclubBundle({
        duplinPicks,
        quantities,
        bundleItems,
        addons,
        extraDuplinFromOrder,
        explicitDuplinQuantities: subscriptionOrderHydrated,
      });
      if (!lines.length) {
        setSubscriptionSaveError("Add at least one item to save.");
        return;
      }
      await updateWineClubSubscriptionOrderLines(subscriptionContractId, lines);
      subscriptionHydrateKeyRef.current = null;
      const cid = getCustomerId();
      if (cid != null && cid !== "") {
        const data = await getFullWineClubInfo(cid);
        setWineClubSubscriptionInfo(data);
      }
    } catch (err) {
      setSubscriptionSaveError(err?.message || "Could not save subscription order.");
    } finally {
      setSubscriptionSaving(false);
    }
  };

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 750);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const listElement = document.querySelector('.perfect-pairings-list');
    if (listElement) {
      const itemWidth = listElement.querySelector('.pairing-item')?.offsetWidth || 0;
      const computedStyle = getComputedStyle(listElement);
      const gapValue = computedStyle.getPropertyValue('--pairing-gap').trim();
      const gap = gapValue ? parseInt(gapValue, 10) : 10;
      listElement.scrollTo({
        left: pairingScrollIndex * (itemWidth + gap),
        behavior: 'smooth'
      });
    }
  }, [pairingScrollIndex]);

  const addProduct = (product) => {
    if (isSoldOut(product)) return;
    if (product.isAddon) {
      setAddons([...addons, product]);
      return;
    }
    setBundleItems([...bundleItems, product]);
    setQuantities(prev => ({
      ...prev,
      [product.id]: (prev[product.id] || 0) + 1
    }));
  };

  const removeProduct = (product) => {
    if (product.required) return;
    const index = bundleItems.findIndex(item => item.id === product.id);
    if (index > -1) {
      const newBundle = [...bundleItems];
      newBundle.splice(index, 1);
      setBundleItems(newBundle);
      setQuantities(prev => ({
        ...prev,
        [product.id]: Math.max(0, (prev[product.id] || 0) - 1)
      }));
    }
  };

  const removeAddon = (addon) => {
    const index = addons.findIndex(item => item.id === addon.id);
    if (index > -1) {
      const newAddons = [...addons];
      newAddons.splice(index, 1);
      setAddons(newAddons);
    }
  };

  const getQuantity = (product) => {
    return quantities[product.id] || 0;
  };

  const isInYourPicks = (product) => bundleItems.some(item => item.id === product.id);

  const getYourPicksQuantity = (product) => {
    if (!isInYourPicks(product)) return 0;
    return bundleItems.filter(item => item.id === product.id).length;
  };

  const getProductPrice = (item) => parsePrice(item?.price);

  const calculateTotal = () => {
    const bundleTotal = bundleItems.reduce((total, item) => {
      const quantity = quantities[item.id] || 1;
      return total + getProductPrice(item) * quantity;
    }, 0);
    const addonsTotal = addons.reduce((total, item) => {
      return total + getProductPrice(item) * 0.8;
    }, 0);
    return bundleTotal + addonsTotal;
  };

  const getTierBottleCount = () => {
    const duplinSum = duplinPicks.reduce((sum, p) => sum + getDuplinQuantityForCheckout(p), 0);
    const extraD = extraDuplinFromOrder.reduce(
      (s, r) => s + Math.max(1, Number(r.quantity) || 1),
      0
    );
    const nonDuplinBundle = bundleItems.filter((item) => !duplinPicks.find((dp) => dp.id === item.id));
    const yourSum = nonDuplinBundle.reduce((sum, item) => sum + (quantities[item.id] || 1), 0);
    return duplinSum + extraD + yourSum;
  };

  const calculateFinalTotal = () => {
    const currentItems = getTierBottleCount();

    const duplinPicksSubtotal = duplinPicks.reduce(
      (total, item) => total + getProductPrice(item) * getDuplinQuantityForCheckout(item),
      0
    );
    const extraDuplinSubtotal = extraDuplinFromOrder.reduce(
      (total, row) =>
        total + getProductPrice(row) * Math.max(1, Number(row.quantity) || 1),
      0
    );
    const uniqueProductIds = [...new Set(bundleItems.map((item) => item.id))];
    let bundleSubtotal = uniqueProductIds.reduce((total, productId) => {
      const product = bundleItems.find((item) => item.id === productId);
      if (product) {
        const yourPicksQuantity = bundleItems.filter((item) => item.id === productId).length;
        return total + getProductPrice(product) * yourPicksQuantity;
      }
      return total;
    }, 0);
    const addonsSubtotal = addons.reduce((total, item) => total + getProductPrice(item), 0);
    const originalSubtotal = duplinPicksSubtotal + extraDuplinSubtotal + bundleSubtotal + addonsSubtotal;

    const totalBundleSubtotal = duplinPicksSubtotal + extraDuplinSubtotal + bundleSubtotal;
    const tier = getTierForCount(wineClubTiers, currentItems);
    const bundleDiscount = tier
      ? totalBundleSubtotal * (tier.discountPercent / 100)
      : 0;
    const shipping = tier ? computeShippingCost(tier, originalSubtotal) : 0;

    const totalDiscount = bundleDiscount;
    return originalSubtotal - totalDiscount + shipping;
  };

  const handleProductClick = (product, event) => {
    if (event.target.closest('.quantity-controls') || event.target.closest('.remove-item-btn')) {
      return;
    }
    if (isSoldOut(product)) return;
    addProduct(product);
  };

  const filterTabs = [
    { id: "all", label: "All Products" },
    { id: "red", label: "Red" },
    { id: "white", label: "White" },
    { id: "speciality", label: "Speciality" },
  ];

  const renderCategoryTabs = () => (
    <div className="category-tabs">
      {filterTabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={`category-tab ${selectedCategory === tab.id ? "active" : ""}`}
          onClick={() => setSelectedCategory(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  const filteredProducts = React.useMemo(
    () => filterProductsByTag(productsForGrid, selectedCategory),
    [productsForGrid, selectedCategory]
  );

  const formatPrice = (price) => {
    const n = parsePrice(price);
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
  };

  const renderProductsList = () => {
    if (!filteredProducts.length) {
      return (
        <div className="products-grid products-grid--empty">
          <p>
            {wineClubSettingsLoading
              ? "Loading wine club…"
              : products.length === 0
                ? "No products loaded. Add a script tag with id=\"wine-products-data\" containing a JSON array of products."
                : productsForGrid.length === 0
                  ? "No products match the wine club plan. Only products with a subscription plan are shown."
                  : "No products in this category."}
          </p>
        </div>
      );
    }
    return (
      <div className="products-grid">
        {filteredProducts.map((product) => {
          const inYourPicks = isInYourPicks(product);
          const yourPicksQuantity = getYourPicksQuantity(product);
          const priceNum = getProductPrice(product);
          const soldOut = isSoldOut(product);
          return (
            <div
              key={product.id}
              className={`product-card${soldOut ? " product-card--sold-out" : ""}`}
              onClick={(e) => !soldOut && handleProductClick(product, e)}
            >
              <div className="product-image">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={product.handle ? `/${product.handle}` : `#${product.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={product.image || ""} alt={product.title || ""} />
                </a>
                {isDuplinPick(product) && (
                  <span className="product-badge product-badge--duplin">Duplin Pick</span>
                )}
                {soldOut && (
                  <span className="product-badge product-badge--sold-out">Sold Out</span>
                )}
                {inYourPicks && yourPicksQuantity > 0 && (
                  <div className="quantity-badge">{yourPicksQuantity}</div>
                )}
              </div>
              <div className="product-info">
                <h3 className="product-name">{product.title || product.id}</h3>
                <p className="product-price">{formatPrice(product.price)}</p>
                <div className="product-actions">
                  {!inYourPicks ? (
                    <button
                      type="button"
                      className={`add-to-bundle-btn${soldOut ? " disabled" : ""}`}
                      disabled={soldOut}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!soldOut) addProduct(product);
                      }}
                    >
                      {soldOut ? "Sold Out" : "Add to Bundle"}
                    </button>
                  ) : (
                    <div className="quantity-controls">
                      <button
                        type="button"
                        className="quantity-btn minus"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeProduct(product);
                        }}
                      >
                        -
                      </button>
                      <span className="quantity-inner">{yourPicksQuantity}</span>
                      <button
                        type="button"
                        className="quantity-btn plus"
                        onClick={(e) => {
                          e.stopPropagation();
                          addProduct(product);
                        }}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDuplinPicks = () => {
    if (!duplinPicks.length && !extraDuplinFromOrder.length) return null;
    const displayQtyForProduct = (product) => {
      if (subscriptionOrderHydrated) {
        return Math.max(0, Number(quantities[product.id]) || 0);
      }
      const q = quantities[product.id];
      return q != null && q !== "" ? Math.max(1, Number(q) || 1) : 1;
    };
    const bumpDuplinQty = (product, delta) => {
      setQuantities((prev) => {
        const cur = subscriptionOrderHydrated
          ? Math.max(0, Number(prev[product.id]) || 0)
          : prev[product.id] != null && prev[product.id] !== ""
            ? Math.max(1, Number(prev[product.id]) || 1)
            : 1;
        const next = Math.max(0, cur + delta);
        if (!subscriptionOrderHydrated && next < 1) {
          return { ...prev, [product.id]: 1 };
        }
        return { ...prev, [product.id]: next };
      });
    };
    const bumpExtraDuplinQty = (rowId, delta) => {
      setExtraDuplinFromOrder((rows) =>
        rows.map((r) => {
          if (r.id !== rowId) return r;
          const cur = Math.max(1, Number(r.quantity) || 1);
          const next = Math.max(1, cur + delta);
          return { ...r, quantity: next };
        })
      );
    };
    return (
      <div className="bundle-display">
        <h3>Duplin Picks</h3>
        {subscriptionOrderHydrated && (
          <p className="sub_heading" style={{ marginTop: 0 }}>
            Loaded from your latest subscription order. Adjust quantities, then save.
          </p>
        )}
        <div className="bundle-grid">
          {duplinPicks.map((product) => (
            <div key={product.id} className="bundle-item">
              <div className="bundle-item-image">
                <a target="_blank" rel="noopener noreferrer" href={product.handle ? `/${product.handle}` : `#${product.id}`}>
                  <img src={product.image || ""} alt={product.title || ""} />
                </a>
              </div>
              <div className="bundle-item-info">
                <div className="product-name">{product.title || product.id}</div>
                <div className="product-price">{formatPrice(product.price)}</div>
                <div className="quantity-controls" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="quantity-btn minus"
                    onClick={() => bumpDuplinQty(product, -1)}
                    disabled={
                      subscriptionOrderHydrated
                        ? displayQtyForProduct(product) < 1
                        : displayQtyForProduct(product) <= 1
                    }
                  >
                    -
                  </button>
                  <span className="quantity-inner">{displayQtyForProduct(product)}</span>
                  <button type="button" className="quantity-btn plus" onClick={() => bumpDuplinQty(product, 1)}>
                    +
                  </button>
                </div>
              </div>
            </div>
          ))}
          {extraDuplinFromOrder.map((row) => (
            <div key={row.id} className="bundle-item">
              <div className="bundle-item-image">
                <img src={row.image || ""} alt={row.title || ""} />
              </div>
              <div className="bundle-item-info">
                <div className="product-name">{row.title || row.variantId}</div>
                <div className="product-price">{formatPrice(row.price)}</div>
                <div className="quantity-controls" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="quantity-btn minus"
                    onClick={() => bumpExtraDuplinQty(row.id, -1)}
                    disabled={Math.max(1, Number(row.quantity) || 1) <= 1}
                  >
                    -
                  </button>
                  <span className="quantity-inner">{Math.max(1, Number(row.quantity) || 1)}</span>
                  <button type="button" className="quantity-btn plus" onClick={() => bumpExtraDuplinQty(row.id, 1)}>
                    +
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBundleDisplay = () => {
    let emptySlots;
    if (bundleItems.length === 0) {
      emptySlots = 2;
    } else if (bundleItems.length === 1) {
      emptySlots = 2;
    } else if (bundleItems.length === 2) {
      emptySlots = 2;
    } else {
      emptySlots = 1;
    }
    
    return (
      <div className="bundle-display">
        <div className="users-pick-header">
          <h3>Your Picks</h3>
          <p>You can swap these wines for your preferred ones.</p>
        </div>
        <div className="bundle-grid">
          {bundleItems.map((item, index) => (
            <div key={`${item.id}-${index}`} className="bundle-item">
              <div className="bundle-item-image">
                <a target="_blank" rel="noopener noreferrer" href={item.handle ? `/${item.handle}` : `#${item.id}`}>
                  <img src={item.image || ""} alt={item.title || ""} />
                </a>
                <button
                  type="button"
                  className="remove-item-btn"
                  onClick={() => removeProduct(item)}
                >
                  ×
                </button>
              </div>
              <div className="bundle-item-info">
                <div className="product-name">{item.title || item.id}</div>
                <div className="product-price">{formatPrice(item.price)}</div>
              </div>
            </div>
          ))}
          {Array.from({ length: emptySlots }).map((_, index) => (
            <div key={`empty-${index}`} className="bundle-item">
              <div className="pickup_prod custom_selection is_empty">
                <div
                  className="bundle-item-image"
                  onClick={() => document.querySelector('.products-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M3.75 12H20.25" stroke="#50C1BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                      <path d="M12 3.75V20.25" stroke="#50C1BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                    </svg>
                  </span>
                  <span>Add Wine</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMobileBundle = () => {
    return (
      <div className="mobile-bundle">
        <h3>Your Bundle</h3>
        <div className="mobile-bundle-scroll">
          {bundleItems.map((item, index) => (
            <div key={`${item.id}-${index}`} className="mobile-bundle-item">
              <div className="bundle-item-image">
                <img src={item.image || ""} alt={item.title || ""} />
                <button
                  type="button"
                  className="remove-item-btn"
                  onClick={() => removeProduct(item)}
                >
                  ×
                </button>
              </div>
              <div className="bundle-item-info">
                <h4>{item.title || item.id}</h4>
                <p>{formatPrice(item.price)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPerfectPairings = () => {
    const pairingProducts = addonProducts.map((p) => ({ ...p, isAddon: true }));
    const totalPairingItems = pairingProducts.length;

    if (totalPairingItems === 0) return null;

    return (
      <>
        <h3>Perfect Pairings</h3>
        <div className="perfect-pairings-carousel-viewport">
          <div className="perfect-pairings-list">
            {pairingProducts.map((product) => {
              const soldOut = isSoldOut(product);
              return (
                <div key={product.id} className={`pairing-item${soldOut ? " pairing-item--sold-out" : ""}`}>
                  <div className="pairing-item-image">
                    <a target="_blank" rel="noopener noreferrer" href={product.handle ? `/${product.handle}` : `#${product.id}`}>
                      <img src={product.image || ""} alt={product.title || ""} />
                    </a>
                  </div>
                  <div className="pairing-item-info">
                    <h4>{product.title || product.id}</h4>
                    <p>{formatPrice(product.price)}</p>
                    <button
                      type="button"
                      className={`add-to-bundle-btn small${soldOut ? " disabled" : ""}`}
                      disabled={soldOut}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!soldOut) addProduct(product);
                      }}
                    >
                      {soldOut ? "Sold Out" : "Add"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="perfect-pairings-pagination">
          <button
            type="button"
            className="arrow left"
            onClick={() => setPairingScrollIndex(Math.max(0, pairingScrollIndex - 1))}
            disabled={pairingScrollIndex === 0}
          >
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.96878 19.1417C8.8991 19.2114 8.84383 19.2941 8.80612 19.3851C8.7684 19.4762 8.74899 19.5737 8.74899 19.6723C8.74899 19.7708 8.7684 19.8684 8.80612 19.9595C8.84383 20.0505 8.8991 20.1332 8.96878 20.2029C9.03847 20.2726 9.12119 20.3279 9.21224 20.3656C9.30328 20.4033 9.40086 20.4227 9.49941 20.4227C9.59796 20.4227 9.69554 20.4033 9.78658 20.3656C9.87763 20.3279 9.96035 20.2726 10.03 20.2029L17.53 12.7029C17.5998 12.6333 17.6551 12.5505 17.6928 12.4595C17.7306 12.3684 17.75 12.2709 17.75 12.1723C17.75 12.0737 17.7306 11.9761 17.6928 11.8851C17.6551 11.794 17.5998 11.7113 17.53 11.6417L10.03 4.14167C9.8893 4.00094 9.69843 3.92187 9.49941 3.92188C9.30039 3.92188 9.10952 4.00094 8.96878 4.14167C8.82805 4.2824 8.74899 4.47327 8.74899 4.67229C8.74899 4.87131 8.82805 5.06219 8.96878 5.20292L15.9391 12.1723L8.96878 19.1417Z" fill="#232323"></path>
            </svg>
          </button>
          {pairingProducts.map((_, index) => (
            <div
              key={index}
              className={`dot ${index === pairingScrollIndex ? "active" : ""}`}
              onClick={() => setPairingScrollIndex(index)}
            />
          ))}
          <button
            type="button"
            className="arrow right"
            onClick={() => setPairingScrollIndex(Math.min(totalPairingItems - 1, pairingScrollIndex + 1))}
            disabled={pairingScrollIndex === totalPairingItems - 1}
          >
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.96878 19.1417C8.8991 19.2114 8.84383 19.2941 8.80612 19.3851C8.7684 19.4762 8.74899 19.5737 8.74899 19.6723C8.74899 19.7708 8.7684 19.8684 8.80612 19.9595C8.84383 20.0505 8.8991 20.1332 8.96878 20.2029C9.03847 20.2726 9.12119 20.3279 9.21224 20.3656C9.30328 20.4033 9.40086 20.4227 9.49941 20.4227C9.59796 20.4227 9.69554 20.4033 9.78658 20.3656C9.87763 20.3279 9.96035 20.2726 10.03 20.2029L17.53 12.7029C17.5998 12.6333 17.6551 12.5505 17.6928 12.4595C17.7306 12.3684 17.75 12.2709 17.75 12.1723C17.75 12.0737 17.7306 11.9761 17.6928 11.8851C17.6551 11.794 17.5998 11.7113 17.53 11.6417L10.03 4.14167C9.8893 4.00094 9.69843 3.92187 9.49941 3.92188C9.30039 3.92188 9.10952 4.00094 8.96878 4.14167C8.82805 4.2824 8.74899 4.47327 8.74899 4.67229C8.74899 4.87131 8.82805 5.06219 8.96878 5.20292L15.9391 12.1723L8.96878 19.1417Z" fill="#232323"></path>
            </svg>
          </button>
        </div>
      </>
    );
  };

  const renderBundleProgress = () => {
    const currentItems = getTierBottleCount();
    const tiers = wineClubTiers;
    const maxBottles = tiers.length ? tiers[0].minBottles : 12;
    const currentTier = getTierForCount(tiers, currentItems);
    const nextTier = getNextTier(tiers, currentItems);

    const progressPercentage = maxBottles > 0 ? Math.min(100, (currentItems / maxBottles) * 100) : 0;
    const milestoneText = currentTier
      ? `Nice! You've unlocked ${currentTier.discountPercent}% off + ${formatTierShippingPreview(currentTier)} for the entire quarter!`
      : '';
    const progressText = nextTier
      ? `Add ${Math.max(0, nextTier.minBottles - currentItems)} more bottles to unlock ${nextTier.discountPercent}% off + ${formatTierShippingPreview(nextTier)}`
      : '';

    return (
      <div className="bundle-progress">
        <div className="progress-bar-container">
          <div
            className="progress-bar"
            style={{
              width: `${progressPercentage}%`,
              minWidth: progressPercentage > 0 ? '4px' : 0,
            }}
          >
            {'\u00A0'}
          </div>
        </div>
        {milestoneText && (
          <div className="progress-text">
            <div className="milestone_text_container">
              <img alt="progress-text" src="https://cdn.shopify.com/s/files/1/0919/9045/8684/files/Component_4.png?v=1755602293" loading="lazy" />
              <p>{milestoneText}</p>
            </div>
          </div>
        )}
        {progressText && (
          <div className="progress-text">
            <span>
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.0269 12.6853L14.1913 10.8984L12.4101 6.05906C12.3046 5.7725 12.1137 5.52518 11.8633 5.35049C11.6128 5.17579 11.3148 5.08213 11.0094 5.08213C10.7041 5.08213 10.4061 5.17579 10.1556 5.35049C9.90514 5.52518 9.7143 5.7725 9.60881 6.05906L7.81631 10.8984L2.97694 12.6797C2.69037 12.7852 2.44305 12.976 2.26836 13.2265C2.09367 13.4769 2 13.7749 2 14.0803C2 14.3857 2.09367 14.6837 2.26836 14.9342C2.44305 15.1846 2.69037 15.3755 2.97694 15.4809L7.81631 17.2734L9.59756 22.1128C9.70305 22.3994 9.89389 22.6467 10.1443 22.8214C10.3948 22.9961 10.6928 23.0897 10.9982 23.0897C11.3036 23.0897 11.6016 22.9961 11.852 22.8214C12.1025 22.6467 12.2933 22.3994 12.3988 22.1128L14.1913 17.2734L19.0307 15.4922C19.3173 15.3867 19.5646 15.1959 19.7393 14.9454C19.914 14.6949 20.0076 14.3969 20.0076 14.0916C20.0076 13.7862 19.914 13.4882 19.7393 13.2377C19.5646 12.9873 19.3173 12.7964 19.0307 12.6909L19.0269 12.6853ZM13.3476 15.9816C13.2458 16.0191 13.1533 16.0782 13.0766 16.155C12.9999 16.2317 12.9407 16.3241 12.9032 16.4259L11.0038 21.5719L9.10819 16.4297C9.07073 16.3268 9.0112 16.2334 8.93378 16.156C8.85636 16.0786 8.76294 16.019 8.66006 15.9816L3.51787 14.0859L8.66006 12.1903C8.76294 12.1529 8.85636 12.0933 8.93378 12.0159C9.0112 11.9385 9.07073 11.8451 9.10819 11.7422L11.0038 6.6L12.8994 11.7422C12.937 11.844 12.9961 11.9364 13.0728 12.0132C13.1496 12.0899 13.242 12.149 13.3438 12.1866L18.4897 14.0859L13.3476 15.9816ZM14.0038 4.33594C14.0038 4.13703 14.0828 3.94626 14.2235 3.80561C14.3641 3.66496 14.5549 3.58594 14.7538 3.58594H16.2538V2.08594C16.2538 1.88703 16.3328 1.69626 16.4735 1.55561C16.6141 1.41496 16.8049 1.33594 17.0038 1.33594C17.2027 1.33594 17.3935 1.41496 17.5341 1.55561C17.6748 1.69626 17.7538 1.88703 17.7538 2.08594V3.58594H19.2538C19.4527 3.58594 19.6435 3.66496 19.7841 3.80561C19.9248 3.94626 20.0038 4.13703 20.0038 4.33594C20.0038 4.53485 19.9248 4.72562 19.7841 4.86627C19.6435 5.00692 19.4527 5.08594 19.2538 5.08594H17.7538V6.58594C17.7538 6.78485 17.6748 6.97562 17.5341 7.11627C17.3935 7.25692 17.2027 7.33594 17.0038 7.33594C16.8049 7.33594 16.6141 7.25692 16.4735 7.11627C16.3328 6.97562 16.2538 6.78485 16.2538 6.58594V5.08594H14.7538C14.5549 5.08594 14.3641 5.00692 14.2235 4.86627C14.0828 4.72562 14.0038 4.53485 14.0038 4.33594ZM23.7538 8.83594C23.7538 9.03485 23.6748 9.22562 23.5341 9.36627C23.3935 9.50692 23.2027 9.58594 23.0038 9.58594H22.2538V10.3359C22.2538 10.5349 22.1748 10.7256 22.0341 10.8663C21.8935 11.0069 21.7027 11.0859 21.5038 11.0859C21.3049 11.0859 21.1141 11.0069 20.9735 10.8663C20.8328 10.7256 20.7538 10.5349 20.7538 10.3359V9.58594H20.0038C19.8049 9.58594 19.6141 9.50692 19.4735 9.36627C19.3328 9.22562 19.2538 9.03485 19.2538 8.83594C19.2538 8.63703 19.3328 8.44626 19.4735 8.30561C19.6141 8.16496 19.8049 8.08594 20.0038 8.08594H20.7538V7.33594C20.7538 7.13703 20.8328 6.94626 20.9735 6.80561C21.1141 6.66496 21.3049 6.58594 21.5038 6.58594C21.7027 6.58594 21.8935 6.66496 22.0341 6.80561C22.1748 6.94626 22.2538 7.13703 22.2538 7.33594V8.08594H23.0038C23.2027 8.08594 23.3935 8.16496 23.5341 8.30561C23.6748 8.44626 23.7538 8.63703 23.7538 8.83594Z" fill="#50C1BC"></path>
              </svg>
            </span>
            <p>{progressText}</p>
          </div>
        )}
      </div>
    );
  };

  const renderBundleSummary = () => {
    const currentItems = getTierBottleCount();

    const duplinPicksSubtotal = duplinPicks.reduce(
      (total, item) => total + getProductPrice(item) * getDuplinQuantityForCheckout(item),
      0
    );
    const extraDuplinSubtotal = extraDuplinFromOrder.reduce(
      (total, row) =>
        total + getProductPrice(row) * Math.max(1, Number(row.quantity) || 1),
      0
    );
    const uniqueProductIds = [...new Set(bundleItems.map((item) => item.id))];
    let bundleSubtotal = uniqueProductIds.reduce((total, productId) => {
      const product = bundleItems.find((item) => item.id === productId);
      if (product) {
        const yourPicksQuantity = bundleItems.filter((item) => item.id === productId).length;
        return total + getProductPrice(product) * yourPicksQuantity;
      }
      return total;
    }, 0);
    const addonsSubtotal = addons.reduce((total, item) => total + getProductPrice(item), 0);
    const originalSubtotal = duplinPicksSubtotal + extraDuplinSubtotal + bundleSubtotal + addonsSubtotal;
    const tier = getTierForCount(wineClubTiers, currentItems);
    const discountPercentage = tier ? tier.discountPercent : 0;
    const totalBundleSubtotal = duplinPicksSubtotal + extraDuplinSubtotal + bundleSubtotal;
    const bundleDiscount = tier ? totalBundleSubtotal * (tier.discountPercent / 100) : 0;
    const shipping = tier ? computeShippingCost(tier, originalSubtotal) : 0;

    const totalDiscount = bundleDiscount;

    const newSubtotal = originalSubtotal - totalDiscount + shipping;
    const originalTotalCalculated = originalSubtotal;
    const newTotalCalculated = newSubtotal;
    const differenceCalculated = (newTotalCalculated - originalTotalCalculated).toFixed(2);

    return (
      <div className="bundle-summary-section">
        <div className="bundle-summary">
          <div className="bundle-total">
            <span>Total:</span>
            <span>${newSubtotal.toFixed(2)}</span>
          </div>
        </div>
        <div className="bundle-summary-totals">
          <div className="original-total">
            <div className="original-total-container">
              <span>Original Total</span>
              <span>${originalTotalCalculated.toFixed(2)}</span>
            </div>
            <div className="recommendation-text">
              Based on Duplin recommendations
            </div>
          </div>
          <div className="arrow-icon">→</div>
          <div className="new-total">
            <span>New Total</span>
            <span>${newTotalCalculated.toFixed(2)} ({differenceCalculated >= 0 ? '+' : ''}{differenceCalculated})</span>
          </div>
        </div>

        <div className="bundle-breakdown">
          <div className="breakdown-item">
            <span>Subtotal</span>
            <span>${originalSubtotal.toFixed(2)}</span>
          </div>
          {bundleDiscount > 0 && discountPercentage > 0 && (
            <div className="breakdown-item">
              <span>{discountPercentage}% Wine Club Discount</span>
              <span>- ${bundleDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="breakdown-item">
            <span>Shipping</span>
            <span>{shipping === 0 ? '-' : `$${shipping.toFixed(2)}`}</span>
          </div>
        </div>

        <div className="new-subtotal">
          <span>New Subtotal</span>
          <span>${newSubtotal.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const renderAddons = () => {
    return (
      <div className="addons-section">
        <h4>Add-ons</h4>
        <div className="addons-list">
          {addons.map((addon) => (
            <div key={addon.id} className="addon-item">
              <div className="addon-item-image">
                <img src={addon.image || ""} alt={addon.title || addon.id} />
              </div>
              <div className="addon-item-info">
                <div className="addon-item-name">{addon.title || addon.id}</div>
                <div className="addon-item-prices">
                  <span className="addon-item-price">{formatPrice(addon.price)}</span>
                </div>
                <button type="button" className="remove-addon-btn" onClick={() => removeAddon(addon)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const yourPicksCount = bundleItems.length;
  const missingToMinimum = Math.max(0, MINIMUM_BOTTLES - yourPicksCount);
  const needsEvenCount = yourPicksCount >= MINIMUM_BOTTLES && yourPicksCount % 2 !== 0;
  const isCheckoutDisabled = missingToMinimum > 0 || needsEvenCount || isCheckoutLoading;
  const desktopCheckoutLabel = isCheckoutLoading
    ? "Loading…"
    : missingToMinimum > 0
      ? `Add ${missingToMinimum} more items to continue`
      : needsEvenCount
        ? "Add 1 more item to make Your Picks even"
        : "Proceed to Checkout";
  const mobileCheckoutLabel = isCheckoutLoading
    ? "Loading…"
    : missingToMinimum > 0
      ? `Add ${missingToMinimum} more items to continue`
      : needsEvenCount
        ? "Add 1 more item to make Your Picks even"
        : `Proceed to Checkout - $${calculateFinalTotal().toFixed(2)}`;

  return (
    <div className="build-your-bundle">
      <div className="bundle-header">
        <h1>Pick your flavors</h1>
        <p className="sub_heading">Mix and match and get your taste buds going!</p>
        <p className="highlight_text">Minimum 2 bottles required.</p>
        {subscriptionInfoLoading && (
          <p className="sub_heading" role="status">Loading your subscription…</p>
        )}
        {subscriptionInfoError && (
          <p className="checkout-error" role="alert">{subscriptionInfoError}</p>
        )}
      </div>
      <div className="bundle-content">
        {isMobileView ? (
          <>
            {renderMobileBundle()}
            {renderCategoryTabs()}
            {renderProductsList()}
            {/* Perfect Pairings Section for Mobile */}
            <div className="perfect-pairings-section">
              {renderPerfectPairings()}
            </div>
          </>
        ) : (
          <>
            <div className="products-section">
              {renderCategoryTabs()}
              {renderProductsList()}
            </div>
            <div className="bundle-section">
              {renderDuplinPicks()}
              {renderBundleDisplay()}
              {renderBundleProgress()}
              {renderAddons()}
              {renderBundleSummary()}
              <div className="bundle-footer" ref={checkoutErrorRef}>
                {hasActiveWineSubscription ? (
                  <>
                    {subscriptionSaveError && (
                      <p className="checkout-error" role="alert">{subscriptionSaveError}</p>
                    )}
                    <button
                      type="button"
                      className="checkout-btn"
                      disabled={subscriptionSaving}
                      onClick={onSaveSubscriptionOrder}
                    >
                      {subscriptionSaving ? "Saving…" : "Save subscription order"}
                    </button>
                  </>
                ) : (
                  <>
                    {checkoutError && (
                      <p className="checkout-error" role="alert">{checkoutError}</p>
                    )}
                    <button
                      type="button"
                      className="checkout-btn"
                      disabled={isCheckoutDisabled}
                      onClick={onCheckoutClick}
                    >
                      {desktopCheckoutLabel}
                    </button>
                  </>
                )}
              </div>
              <div className="perfect-pairings-section">
                {renderPerfectPairings()}
              </div>
            </div>
          </>
        )}
      </div>
      {isMobileView && (
        <div className="mobile-footer">
          {renderBundleProgress()}
          {renderBundleSummary()}
          {renderAddons()}
          <div className="bundle-footer" ref={checkoutErrorRef}>
            {hasActiveWineSubscription ? (
              <>
                {subscriptionSaveError && (
                  <p className="checkout-error" role="alert">{subscriptionSaveError}</p>
                )}
                <button
                  type="button"
                  className="checkout-btn"
                  disabled={subscriptionSaving}
                  onClick={onSaveSubscriptionOrder}
                >
                  {subscriptionSaving ? "Saving…" : "Save subscription order"}
                </button>
              </>
            ) : (
              <>
                {checkoutError && (
                  <p className="checkout-error" role="alert">{checkoutError}</p>
                )}
                <button
                  type="button"
                  className="checkout-btn"
                  disabled={isCheckoutDisabled}
                  onClick={onCheckoutClick}
                >
                  {mobileCheckoutLabel}
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default BuildYourBundle;
